import Shell from "../../../../../components/specific/Shell.tsx";
import {User} from "../../../../../api/accessControl/User.ts";
import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {XCircleIcon} from "@heroicons/react/24/solid";
import {BackendApi} from "../../../../../api/api.ts";
import {usePartnerClientScope} from "../../../../../hooks/usePartnerClientScope.ts";
import {ClientSubtitle} from "../../../../../components/specific/ClientSwitcher";
import {PartnerClientToolbar, getVisibleGoToItemIds} from "../../../../../components/specific/PartnerClientToolbar";
import {useMatchesPageState} from "./MatchesPageState.ts";
import {SecondaryButton} from "../../../../../components/general/SecondaryButton.tsx";
import {PrimaryButton} from "../../../../../components/general/PrimaryButton.tsx";
import {Match} from "./Match.tsx";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";
import {useDocumentDrawer} from "../../../../../views/DocumentDrawer";
import { formatDateForInput, parseDate } from "../../../../../components/general/DateRangeInput/utils/dateRangeUtils.ts";
import { useFilterStore } from "../../../../../contexts/FilterStore.tsx";
import { DateRangeInput } from "../../../../../components/general/DateRangeInput/DateRangeInput.tsx";


function MatchesBusinessGroup({key, customer, onOpenDocument, onUnmatch}: {
    key: number,
    customer: any,
    onOpenDocument: (businessId: string, documentId: string) => void,
    onUnmatch?: (matchId: string, transactionIds: string[], billIds: string[]) => void
}) {
    return (
        <div className="mb-10" key={key}>
            <h2 className="text-2xl text-gray-800 mb-4">{customer.customer_name}</h2>
            {customer.matches.length > 0 ? (
                customer.matches.map((match, index: number) => (
                    <Match key={index} match={match} businessId={customer.business_id} onOpenDocument={onOpenDocument} onUnmatch={onUnmatch}/>
                ))
            ) : (
                <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
                    <p className="text-gray-500">Geen matches voor deze klant.</p>
                </div>
            )}
        </div>
    );
}

export default function MatchesPage({currentUser}: { currentUser: User }) {
    const now = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const default_from_date = formatDateForInput(yesterday);
    const default_until_date = formatDateForInput(now);
    const api = new BackendApi(currentUser.getToken());


    const [searchParams, setSearchParams] = useSearchParams();
    const [matchesURLState, setMatchesURLState] = useMatchesPageState(searchParams, setSearchParams);
    const { state: filterStore, dispatch: filterDispatch } = useFilterStore();

    const initialDateRange = useMemo(() => ({
        from: parseDate(matchesURLState.startDate || filterStore.common.dateRange.from),
        to: parseDate(matchesURLState.endDate || filterStore.common.dateRange.to)
    }), [matchesURLState.startDate, matchesURLState.endDate, filterStore.common.dateRange.from, filterStore.common.dateRange.to]);

    const [dateRange, setDateRange] = useState(initialDateRange);
    const clientScope = usePartnerClientScope(currentUser);

    const [data, setData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const documentDrawer = useDocumentDrawer();
    const {dispatch} = useNotifications();

    useEffect(() => {
        setDateRange({
            from: parseDate(matchesURLState.startDate || filterStore.common.dateRange.from),
            to: parseDate(matchesURLState.endDate || filterStore.common.dateRange.to)
        });
    }, [matchesURLState.startDate, matchesURLState.endDate, filterStore.common.dateRange]);

    const handleUnmatch = async (matchId: string, transactionIds: string[], billIds: string[]) => {
        try {
            await api.Reconciliation__manuallyUnmatch({
                match_id: matchId,
                transaction_ids: transactionIds,
                bill_ids: billIds
            });
            
            dispatch({
                title: 'Match ontkoppeld',
                description: 'De match is succesvol ontkoppeld.',
                type: 'success'
            });
            
            await onLoad(clientScope.selectedClientId, matchesURLState.startDate, matchesURLState.endDate);
        } catch (error) {
            dispatch({
                title: 'Fout opgetreden',
                description: 'Er is een fout opgetreden bij het ontkoppelen. Probeer het later opnieuw.',
                type: 'error'
            });
            console.error('Unmatch error:', error);
        }
    };

    async function onLoad(business_id: string | null | undefined = undefined, date_from: string | null = null, date_until: string | null = null) {
        const final_from_date: string = date_from || formatDateForInput(dateRange.from) || default_from_date;
        const final_until_date: string = date_until || formatDateForInput(dateRange.to) || default_until_date;
        const final_business_id: string | null = business_id !== undefined ? business_id : clientScope.selectedClientId;

        setMatchesURLState({
            businessId: final_business_id,
            startDate: final_from_date,
            endDate: final_until_date
        });

        
        filterDispatch({
            type: 'SET_FILTERS',
            payload: {
                common: {
                    customerId: final_business_id,
                    dateRange: {
                        from: final_from_date,
                        to: final_until_date
                    }
                }
            }
        });

        setIsLoading(true);
        setError(null);


        try {
            const response = await api.Partner__client__matches(final_from_date, final_until_date, final_business_id);

            setData(response);
        } catch (err) {
            setError("Er is een fout opgetreden. Probeer het later opnieuw.");
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        onLoad(clientScope.selectedClientId);
    }, [clientScope.selectedClientId]);


    return (
        <Shell
            currentUser={currentUser}
            isLoading={isLoading}
            title="Matches"
            subtitle={<ClientSubtitle prefix="Matches van" clientName={clientScope.selectedClient?.name ?? null} fallback="Facturen en documenten die TransactionIntelligence heeft gematched."/>}
            titleActions={
                <PartnerClientToolbar
                    clients={clientScope.clients}
                    selectedClientId={clientScope.selectedClientId}
                    recentClientIds={clientScope.recentClientIds}
                    goToItemIds={getVisibleGoToItemIds((flag) => currentUser.flags.getFlag(flag))}
                    onSelectClient={clientScope.selectClient}
                />
            }
        >


            <div className="mb-4 bg-white w-full flex justify-between items-center gap-4">
                <div className="flex items-end flex-wrap gap-4">
                    <div className="w-[234px]">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Periode
                        </label>
                        <DateRangeInput
                            value={dateRange}
                            onChange={setDateRange}
                            className="py-1.5 min-h-[38px]"
                        />
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <SecondaryButton title={"Wissen"} onClick={() => {
                        filterDispatch({ type: 'RESET' });
                        onLoad(clientScope.selectedClientId, default_from_date, default_until_date);
                    }}/>
                    <PrimaryButton title={"Toepassen"} onClick={() => {
                        onLoad(clientScope.selectedClientId);
                    }}/>
                </div>
            </div>

            {error ? (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true"/>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <div className="mt-2 text-sm text-red-700">
                                <p>{error}</p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : data.length === 0 ? (
                <p className="text-gray-500 text-center pt-12">Geen matches gevonden.</p>
            ) : (
                <div className="mt-12">
                    {data.map((customer, index) => (
                        <MatchesBusinessGroup key={index} customer={customer} onOpenDocument={documentDrawer.open} onUnmatch={handleUnmatch}/>
                    ))}
                </div>
            )}
        </Shell>
    );
}
